import { FileText, MessageSquareMore, Plus, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

export type ProjectWorkspaceTabKind = "timeline" | "draft" | "artifact";

export interface ProjectWorkspaceTab {
  id: string;
  kind: ProjectWorkspaceTabKind;
  label: string;
  artifactId?: string;
}

interface PersistedWorkspaceTabs {
  version: 1;
  activeTabId: string;
  tabs: ProjectWorkspaceTab[];
}

function workspaceTabId(prefix: string) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function initialTabs(): PersistedWorkspaceTabs {
  const tab: ProjectWorkspaceTab = {
    id: "timeline",
    kind: "timeline",
    label: "研究进展",
  };
  return { version: 1, activeTabId: tab.id, tabs: [tab] };
}

function parsePersistedTabs(value: string | null): PersistedWorkspaceTabs {
  if (!value) return initialTabs();
  try {
    const parsed = JSON.parse(value) as Partial<PersistedWorkspaceTabs>;
    if (
      parsed.version !== 1 ||
      typeof parsed.activeTabId !== "string" ||
      !Array.isArray(parsed.tabs)
    ) {
      return initialTabs();
    }
    const tabs = parsed.tabs.flatMap<ProjectWorkspaceTab>((tab) => {
      if (
        !tab ||
        typeof tab.id !== "string" ||
        typeof tab.label !== "string" ||
        !["timeline", "draft", "artifact"].includes(tab.kind)
      ) {
        return [];
      }
      if (tab.kind === "artifact" && typeof tab.artifactId !== "string") return [];
      return [{ id: tab.id, kind: tab.kind, label: tab.label, artifactId: tab.artifactId }];
    });
    if (!tabs.length || !tabs.some((tab) => tab.id === parsed.activeTabId)) {
      return initialTabs();
    }
    return { version: 1, activeTabId: parsed.activeTabId, tabs };
  } catch {
    return initialTabs();
  }
}

function readTabs(key: string) {
  if (typeof window === "undefined") return initialTabs();
  return parsePersistedTabs(window.localStorage.getItem(key));
}

export function useProjectWorkspaceTabs(ownerId: string, projectId: string) {
  const key = `cadence.${ownerId}.${projectId}.workspace-tabs.v1`;
  const [state, setState] = useState<PersistedWorkspaceTabs>(() => readTabs(key));
  const previousKey = useRef(key);

  useEffect(() => {
    if (previousKey.current === key) return;
    previousKey.current = key;
    setState(readTabs(key));
  }, [key]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Tabs are a local workbench preference. Storage failure must not affect
      // the Project, its Runtime, or any scientific record.
    }
  }, [key, state]);

  const activate = useCallback((tabId: string) => {
    setState((current) =>
      current.tabs.some((tab) => tab.id === tabId)
        ? { ...current, activeTabId: tabId }
        : current,
    );
  }, []);

  const createDraft = useCallback(() => {
    const tab: ProjectWorkspaceTab = {
      id: workspaceTabId("draft"),
      kind: "draft",
      label: "新研究",
    };
    setState((current) => ({
      ...current,
      activeTabId: tab.id,
      tabs: [...current.tabs, tab],
    }));
    return tab;
  }, []);

  const openArtifact = useCallback((artifactId: string, label: string) => {
    setState((current) => {
      const existing = current.tabs.find(
        (tab) => tab.kind === "artifact" && tab.artifactId === artifactId,
      );
      if (existing) return { ...current, activeTabId: existing.id };
      const tab: ProjectWorkspaceTab = {
        id: workspaceTabId("artifact"),
        kind: "artifact",
        label,
        artifactId,
      };
      return { ...current, activeTabId: tab.id, tabs: [...current.tabs, tab] };
    });
  }, []);

  const close = useCallback((tabId: string) => {
    setState((current) => {
      const index = current.tabs.findIndex((tab) => tab.id === tabId);
      if (index < 0) return current;
      const remaining = current.tabs.filter((tab) => tab.id !== tabId);
      const tabs = remaining.length ? remaining : initialTabs().tabs;
      const activeTabId =
        current.activeTabId === tabId
          ? tabs[Math.max(0, Math.min(index - 1, tabs.length - 1))].id
          : current.activeTabId;
      return { ...current, tabs, activeTabId };
    });
  }, []);

  const activeTab = useMemo(
    () => state.tabs.find((tab) => tab.id === state.activeTabId) ?? state.tabs[0],
    [state],
  );

  return {
    activeTab,
    tabs: state.tabs,
    activate,
    close,
    createDraft,
    openArtifact,
  };
}

function tabIcon(kind: ProjectWorkspaceTabKind) {
  return kind === "artifact" ? (
    <FileText size={14} aria-hidden="true" />
  ) : (
    <MessageSquareMore size={14} aria-hidden="true" />
  );
}

export function ProjectWorkspaceTabs({
  tabs,
  activeTabId,
  onActivate,
  onClose,
  onNew,
}: {
  tabs: ProjectWorkspaceTab[];
  activeTabId: string;
  onActivate(tab: ProjectWorkspaceTab): void;
  onClose(tab: ProjectWorkspaceTab): void;
  onNew(): void;
}) {
  const activateFromKeyboard = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let nextIndex: number | undefined;
    if (event.key === "ArrowLeft") {
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (event.key === "ArrowRight") {
      nextIndex = (index + 1) % tabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabs.length - 1;
    }
    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    onActivate(nextTab);
    window.requestAnimationFrame(() => {
      document.getElementById(`workspace-tab-${nextTab.id}`)?.focus();
    });
  };

  return (
    <div className="project-workspace-tabs" data-testid="project-workspace-tabs">
      <div className="project-workspace-tab-scroll" role="tablist" aria-label="项目工作区标签">
        {tabs.map((tab, index) => {
          const active = tab.id === activeTabId;
          return (
            <div
              className={`project-workspace-tab ${active ? "project-workspace-tab-active" : ""}`}
              key={tab.id}
            >
              <button
                type="button"
                role="tab"
                id={`workspace-tab-${tab.id}`}
                aria-selected={active}
                aria-controls="project-workspace-panel"
                tabIndex={active ? 0 : -1}
                className="project-workspace-tab-trigger"
                data-testid={`workspace-tab-${tab.id}`}
                onClick={() => onActivate(tab)}
                onKeyDown={(event) => activateFromKeyboard(event, index)}
                title={tab.label}
              >
                {tabIcon(tab.kind)}
                <span>{tab.label}</span>
              </button>
              <button
                type="button"
                className="project-workspace-tab-close"
                data-testid={`workspace-tab-close-${tab.id}`}
                aria-label={`关闭${tab.label}标签`}
                onClick={() => onClose(tab)}
              >
                <X size={13} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="project-workspace-tab-new"
        data-testid="workspace-tab-new"
        aria-label="新建研究标签"
        title="新建研究标签"
        onClick={onNew}
      >
        <Plus size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
